import { Section, SectionWrapper } from '@/components/layout/Section'
import { LargeText } from '@/components/ui/typography'
import { AnimatedButton } from '@/components/ui/animated-button'
import { CtaGroup, CtaLink } from '@/components/sections/CtaGroup'
import type { Cta } from '@/components/sections/types'
import { cn } from '@/lib/cn'
import { STUDIO_CTA_LABEL, STUDIO_SIGNUP_URL } from '@/content/studio'

/*
 * `.section-container.bottom-cta` — 33 uses, on more than half the site.
 *
 * Always the same shape: dark ground, centred heading, one line of large text,
 * and the gradient-blob button. This one component removes 33 copies.
 */
/*
 * Oct 19, 2026 Studio launch: the default band carries the site promise, the
 * cleared 74% stat, and the same two doors as the header (Studio signup and
 * Book a demo). The old "1:1 personalization so good, it's invisible." band
 * linked "Get a demo" to "#" on ten pages.
 */
const PAIR: Cta[] = [
  { label: STUDIO_CTA_LABEL, href: STUDIO_SIGNUP_URL, kind: 'primary', placement: 'bottom_cta' },
  { label: 'Book a demo', href: '/demo', kind: 'outlineWhite', placement: 'bottom_cta' },
]

export function BottomCTA({
  heading = 'Every email, written for the person reading it.',
  subheading = "In enterprise customers' sends through Singulate, the personalized version had a 74% higher click-through rate than the generic one.",
  cta,
  emphasis = 'default',
  button,
  note,
  surface = 'darkBlue',
  compact = false,
  link,
  id,
}: {
  heading?: React.ReactNode
  subheading?: React.ReactNode
  cta?: Cta
  /**
   * `pair` (the default when no `cta` is given) renders Create your first email
   * + Book a demo; `primary` renders `cta` as the hero's filled button;
   * `animated` (the default when a `cta` is given) renders the gradient blob.
   */
  button?: 'animated' | 'primary' | 'pair'
  /** One factual line under the button, e.g. the trial terms. */
  note?: string | null
  /** `large` sets the heading at the h1 size: the final ask on a landing page. */
  emphasis?: 'default' | 'large'
  /** `dark` is the Ink ground, for a page whose other dark bands are Ink. */
  surface?: 'darkBlue' | 'dark'
  /** Less height above and below, for a page that has already made its case. */
  compact?: boolean
  /** One quiet text link under the button, for a related page. */
  link?: Cta
  id?: string
}) {
  /*
   * `surface="darkBlue"` is the ground every BottomCTA already rendered.
   * Webflow reached it through the `.section-padding-large.dark` compound, so
   * it read as a padding side effect. The `padding` prop that selected it is
   * gone: both of its values ended at the same 10rem, because `.bottom-cta`'s
   * own `py` came last.
   */
  const mode = button ?? (cta ? 'animated' : 'pair')
  const ctas = mode === 'pair' || !cta ? PAIR : [{ ...cta, kind: 'primary' as const }]
  return (
    <Section
      id={id}
      surface={surface}
      padding={compact ? 'lg' : '2xl'}
      // A page's final ask (large, or carrying a link to a related page) gets a
      // hairline so it doesn't run into the footer's near-identical ground.
      className={cn('items-center', (emphasis === 'large' || link) && 'border-0 border-b border-solid border-white/10 max-md:py-section-md')}
    >
      <SectionWrapper layout="center" gap={mode === 'animated' ? 'large' : 'gap'} className="items-center">
        {mode !== 'animated' || !cta ? (
          // One stack with equal gaps above and below the button.
          <div className="flex flex-col items-center gap-4 sm:gap-5">
            <h2 className={cn('text-balance', 'm-0 text-center text-white', emphasis === 'large' && 'text-h1')}>
              {heading}
            </h2>
            {subheading && (
              <LargeText tone="white" align="center" className="mb-0">
                {subheading}
              </LargeText>
            )}
            <CtaGroup ctas={ctas} className={cn('justify-center', emphasis === 'large' && 'mt-4 sm:mt-6')} />
            {link && (
              <p className="m-0 text-center">
                <CtaLink cta={link} className="text-base text-white decoration-white/40 hover:decoration-white focus-ring" />
              </p>
            )}
            {note && <p className="m-0 text-center text-dark-blue-50">{note}</p>}
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-4">
              <h2 className={cn('text-balance', 'm-0 text-center text-white', emphasis === 'large' && 'text-h1')}>
                {heading}
              </h2>
              {subheading && (
                <LargeText tone="white" align="center">
                  {subheading}
                </LargeText>
              )}
            </div>
            <AnimatedButton href={cta.href}>{cta.label}</AnimatedButton>
          </>
        )}
      </SectionWrapper>
    </Section>
  )
}
